import { useState } from "react";
import { Group, Text, Badge, Stack, Select, Box } from "@mantine/core";
import { useCptCodesAdmin } from "../../../hooks/admin/useCptCodes";

interface SpecimenDiagnosisData {
  specimenId: string;
  cptCode: string | null;
  cptDisplayCode?: string | null;
}

interface CptCodesWidgetProps {
  specimens: any[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  onCodeChange?: (specimenId: string, code: string | null) => void;
  isReadOnly?: boolean;
}

function CptCodeRow({
  specimen,
  currentCode,
  displayCode,
  options,
  isReadOnly,
  onCodeChange,
}: {
  specimen: any;
  currentCode: string | null;
  displayCode: string | null;
  options: { value: string; label: string }[];
  isReadOnly?: boolean;
  onCodeChange?: (specimenId: string, code: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Badge
        size="xs"
        variant="dot"
        color="blue"
        style={{ flexShrink: 0, fontFamily: "monospace" }}
      >
        {specimen.number || "?"}
      </Badge>
      <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
        {specimen.source?.name || specimen.sourceName || ""}
      </Text>
      {isReadOnly ? (
        <Badge
          size="xs"
          variant="outline"
          color={currentCode ? "blue" : "orange"}
          style={{ flexShrink: 0 }}
        >
          {displayCode || "—"}
        </Badge>
      ) : editing ? (
        <Box style={{ flexShrink: 0, width: 140 }}>
          <Select
            size="xs"
            data={options}
            value={currentCode}
            onChange={(val) => {
              onCodeChange?.(specimen.id, val);
              setEditing(false);
            }}
            onBlur={() => setEditing(false)}
            placeholder="Select CPT"
            searchable
            clearable
            autoFocus
            styles={{ input: { fontSize: 11 } }}
          />
        </Box>
      ) : (
        <Badge
          size="xs"
          variant="outline"
          color={currentCode ? "blue" : "orange"}
          style={{ flexShrink: 0, cursor: "pointer" }}
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          {displayCode || (
            <span style={{ fontStyle: "italic", opacity: 0.6 }}>None</span>
          )}
        </Badge>
      )}
    </Group>
  );
}

export function CptCodesWidget({
  specimens,
  diagnosisDataMap,
  onCodeChange,
  isReadOnly = false,
}: CptCodesWidgetProps) {
  const { data: allCptCodes = [] } = useCptCodesAdmin();

  const sorted = [...specimens].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );

  const options = allCptCodes
    .filter((c) => c.isActive !== false)
    .map((c) => ({
      value: c.code,
      label: `${c.code} — ${c.description}`,
    }));

  if (sorted.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No specimens
      </Text>
    );
  }

  return (
    <Stack gap={6}>
      {sorted.map((specimen: any) => {
        const diagData = diagnosisDataMap.get(specimen.id);
        const backendCpt = specimen.diagnosis?.cptCode || null;
        const localCpt = diagData?.cptCode ?? null;

        // If local code is in sync with backend, prefer backend-computed display code
        // (which includes modifiers). Otherwise, fall back to local optimistic state.
        const displayCode =
          localCpt === backendCpt && localCpt !== null
            ? specimen.diagnosis?.cptDisplayCode ?? backendCpt
            : diagData?.cptDisplayCode ?? localCpt;

        const slides = (specimen.blocks || [])
          .flatMap((b: any) => b.slides || [])
          .filter(
            (s: any) => s.stain?.stainType === "IHC" && s.status !== "cancelled"
          );

        return (
          <Stack key={specimen.id} gap={2}>
            <CptCodeRow
              specimen={specimen}
              currentCode={localCpt}
              displayCode={displayCode}
              options={options}
              isReadOnly={isReadOnly}
              onCodeChange={onCodeChange}
            />
            {slides.length > 0 && (
              <Box style={{ paddingLeft: 24 }}>
                <Group gap={4} wrap="nowrap">
                  <Text size="10px" c="dimmed">
                    IHC Slides:
                  </Text>
                  {slides.map((s: any) => {
                    const code = s.stain?.cptCode?.code || "—";
                    return (
                      <Badge
                        key={s.id}
                        size="xs"
                        variant="light"
                        color="blue"
                        styles={{ label: { fontSize: 9 } }}
                      >
                        {s.stain?.code || "Stain"}: {code}
                      </Badge>
                    );
                  })}
                </Group>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
